import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { OrganisationViewModel } from '@core/api';
import { FormlyFieldBaseService } from '@core/services/formly-service/formly-field.service';
import { FormlyPreviewAutocompleteProps } from '@core/services/formly-service/formly-service.types';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OrganisationSearchFormlyFieldService extends FormlyFieldBaseService {
  override translatePath = 'organisation.autocomplete';
  private readonly initialPreviewLimit = 100;

  constructor(protected override translateService: TranslateService) {
    super(translateService);
  }

  private getChipLabel(option: OrganisationViewModel): string {
    const parts = [
      option?.name,
      option?.id,
      option?.address?.city,
      option?.address?.postalCode,
      option?.address?.streetName,
    ].filter(Boolean);

    return parts.join(', ');
  }

  autocompleteSearch(
    placeholder: string,
    organisations$: Observable<OrganisationViewModel[]>,
    onSelect?: (
      id: string,
      onInitSearch: boolean,
      value: OrganisationViewModel
    ) => void,
    required?: boolean,
    visibleInConfirm?: boolean,
    hideInPreview?: boolean,
    onDelete?: () => void,
    defaultValue?: string
  ): FormlyFieldConfig[] {
    const translatePath = 'organisation.autocomplete';

    const columnDefinitions = [
      {
        fieldName: 'name',
        labelTranslationKey: 'user.request.create.label__organisation',
        classes: 'col-md-8, text-overflow',
        hideInPreview: hideInPreview ?? true,
      },
      {
        fieldName: 'id',
        labelTranslationKey: 'user.request.create.label__creditor_number',
        classes: 'col-md-4, text-overflow',
        hideInPreview: hideInPreview ?? true,
      },
      {
        fieldName: 'address.city',
        labelTranslationKey: 'user.request.create.label__city',
        classes: 'col-md-4, text-overflow',
        hideInPreview: hideInPreview ?? true,
      },
      {
        fieldName: 'address.postalCode',
        labelTranslationKey: 'user.request.create.label__postalCode',
        classes: 'col-md-4, text-overflow',
        hideInPreview: hideInPreview ?? true,
      },
      {
        fieldName: 'address.streetName',
        labelTranslationKey: 'user.request.create.label__street',
        classes: 'col-md-4, text-overflow',
        hideInPreview: hideInPreview ?? true,
      },
    ];

    const props: FormlyPreviewAutocompleteProps = {
      bindLabel: 'name',
      bindValue: 'id',
      getChipLabel: (option: OrganisationViewModel) =>
        this.getChipLabel(option),
      required: required ?? true,
      visibleInConfirm: visibleInConfirm ?? false,
      translatePath,
      columnDefinitions,
      onSearch: (searchText: string): Observable<OrganisationViewModel[]> => {
        const query = searchText?.toLowerCase() ?? '';
        return organisations$.pipe(
          map(orgs =>
            orgs.filter(
              org =>
                org.name?.toLowerCase().includes(query) ||
                org.id?.toLowerCase().includes(query) ||
                org.address?.city?.toLowerCase().includes(query) ||
                org.address?.postalCode?.toLowerCase().includes(query) ||
                org.address?.streetName?.toLowerCase().includes(query)
            )
          )
        );
      },
      onSelect,
      onDelete,
      defaultList: organisations$.pipe(
        map(orgs => orgs.slice(0, this.initialPreviewLimit))
      ),
      defaultValue: defaultValue ? { id: defaultValue } : undefined,
      label: this.translateService.instant(placeholder),
    };

    return [
      {
        type: 'preview-autocomplete',
        key: 'searchResult',
        props: { ...props },
        validation: {
          messages: {
            required: this.translateService.instant('formErrors.required', {
              label: this.translateService.instant(placeholder),
            }),
          },
        },
      },
    ];
  }
}
